import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Input, Text } from '@chakra-ui/react';
import { FiLock } from 'react-icons/fi';
import baseService from '../../services/base';
import useDebouncedSave from '../../hooks/useDebouncedSave';
import SaveStatusHint from '../Feedback/SaveStatusHint';
import { AUDIT, AUDIT_LABEL } from '../../theme/auditTokens';

/**
 * A medida como o relatório a mostra: milhar com ponto, decimal com vírgula.
 *
 * `maximumFractionDigits: 4` não é enfeite — a coluna é `numeric(18,4)` e o
 * padrão do toLocaleString corta em 3 casas, arredondando medida ambiental em
 * silêncio. Medida sem valor vira travessão, não "0": zero é uma afirmação
 * ("emitimos zero") e ausência é outra coisa.
 */
const formatar = (valor) => {
 if (valor === null || valor === undefined || valor === '') return '—';

 const numero = Number(valor);
 if (Number.isNaN(numero)) return String(valor);

 return numero.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
};

/**
 * GRI-09 — o avaliador informa ou corrige a medida de UM indicador.
 *
 * Uma instância por linha, e não um estado só em `MedidasGri`, porque
 * `useDebouncedSave` salva UM valor por instância — nota da questão
 * (QuestionScoreField) já usa o mesmo desenho pelo mesmo motivo. Uma questão
 * com N indicadores precisa de N auto-saves independentes: editar o segundo
 * indicador não pode reagendar nem cancelar o save do primeiro.
 *
 * Endpoint dedicado (`PATCH question-response/:id/gri-value`), separado do
 * `POST response` da empresa: são dois atores com significados diferentes
 * para o mesmo número (ver comentário do controller). Campo vazio remove a
 * medida arbitrada — devolve a questão ao estado "sem medida", não zero.
 */
function MedidaDoAvaliadorField({ indicador, valorInicial, responseId, idReviewer, isDisabled }) {
 const [valor, setValor] = useState(valorInicial ?? '');
 // true assim que o avaliador mexe no campo — depois disso o valor local
 // manda, mesmo que o pai re-renderize com o dado antigo do SWR.
 const hasLocalEditRef = useRef(false);

 useEffect(() => {
  if (hasLocalEditRef.current) return;
  setValor(valorInicial ?? '');
 }, [valorInicial]);

 const persistir = async (novoValor) => {
  await baseService.patch(`question-response/${responseId}/gri-value`, {
   id_gri_indicator: indicador.id_gri_indicator,
   value: novoValor === '' ? null : novoValor,
   id_reviewer: idReviewer,
  });
 };

 const gravar = useDebouncedSave(persistir);

 function handleChange(evento) {
  hasLocalEditRef.current = true;
  const novoValor = evento.target.value;
  setValor(novoValor);
  if (isDisabled) return;
  gravar.schedule(novoValor);
 }

 function handleBlur() {
  if (isDisabled) return;
  gravar.flush();
 }

 return (
  <Flex justify='space-between' align='baseline' gap='12px' py='4px'>
   <Text fontSize='13px' color={AUDIT.inkMuted}>
    <Text as='span' fontWeight={600} color={AUDIT.ink}>
     {indicador.code}
    </Text>
    {indicador.name ? ` — ${indicador.name}` : ''}
   </Text>

   <Flex align='center' gap='8px' flexShrink={0}>
    {isDisabled ? (
     // GRI-09: o cinza padrão do Chakra em isDisabled não deixa óbvio POR QUE
     // não dá pra editar — a mesma dúvida que já existia na nota da questão,
     // só que ali o texto explicativo está no banner lá em cima do
     // AuditView, longe o bastante do campo pra passar despercebido.
     <Flex align='center' gap='4px' fontSize='11px' fontWeight={600} color={AUDIT.statusAttention}>
      <Box as={FiLock} boxSize='11px' />
      <Text whiteSpace='nowrap'>Vez da empresa</Text>
     </Flex>
    ) : (
     gravar.status !== 'idle' && (
      <SaveStatusHint status={gravar.status} onRetry={gravar.retry} />
     )
    )}
    <Input
     value={valor}
     onChange={handleChange}
     onBlur={handleBlur}
     onKeyDown={(e) => {
      if (e.key === 'Enter') e.target.blur();
     }}
     isDisabled={isDisabled}
     placeholder='Ex.: 1234,56'
     size='xs'
     w='120px'
     textAlign='right'
     bg={AUDIT.surface}
     borderColor={AUDIT.lineStrong}
    />
    {indicador.unit && (
     <Text fontSize='12px' color={AUDIT.inkMuted} whiteSpace='nowrap'>
      {indicador.unit}
     </Text>
    )}
   </Flex>
  </Flex>
 );
}

/**
 * GRI-07 — as medidas que a empresa informou, uma por indicador.
 *
 * POR QUE EXISTE
 *   A resposta da questão e a medida do indicador deixaram de ser a mesma
 *   coisa. Antes o dashboard atribuía a resposta única a TODOS os indicadores
 *   vinculados — dois indicadores, o mesmo número nos dois. Agora cada um tem
 *   o seu, e o avaliador precisa conferir esses números aqui: sem isto, o
 *   único jeito de saber o que foi para o gráfico seria abrir o banco.
 *
 * Casa o valor com o catálogo que já vem no payload (`question.gri`) para
 * mostrar código, nome e unidade. Medida sem indicador correspondente é
 * ignorada em silêncio: significa que o vínculo foi desfeito depois da
 * resposta, e mostrar um número sem dizer do que ele é seria pior que omitir.
 *
 * GRI-09: `editavel` (+ `responseId`/`idReviewer`) troca a leitura por um
 * campo de input por indicador — ver MedidaDoAvaliadorField acima.
 */
const MedidasGri = ({
 gri,
 valores,
 sempreVisivel = false,
 editavel = false,
 responseId,
 idReviewer,
 isDisabled = false,
 // EG-63: quando o chamador já desenha um painel próprio ao redor (a área de
 // auditoria), a caixa verde daqui viraria caixa-dentro-de-caixa. `flat` tira
 // o próprio fundo/borda e usa os tokens da auditoria em vez do verde de GRI.
 flat = false,
}) => {
 const linhas = useMemo(() => {
  const medidas = Array.isArray(valores) ? valores : [];

  const catalogo = new Map(
   (gri || []).map((indicador) => [indicador.id_gri_indicator, indicador])
  );

  // Com `sempreVisivel`, a lista sai do CATÁLOGO da questão e não das medidas:
  // é assim que uma questão ainda sem resposta mostra quais indicadores ela
  // alimenta, em vez de não mostrar nada. Sem a prop, o comportamento é o
  // antigo — só o que tem medida aparece.
  //
  // GRI-09: `editavel` implica o mesmo — sem isto, um indicador ainda sem
  // medida simplesmente não geraria linha nenhuma, e é exatamente ele que o
  // avaliador precisa poder preencher.
  if (sempreVisivel || editavel) {
   return (gri || []).map((indicador) => ({
    ...indicador,
    value: medidas.find(
     (medida) => medida.id_gri_indicator === indicador.id_gri_indicator
    )?.value,
   }));
  }

  if (medidas.length === 0) return [];

  return medidas
   .map((medida) => {
    const indicador = catalogo.get(medida.id_gri_indicator);
    return indicador ? { ...indicador, value: medida.value } : null;
   })
   .filter(Boolean);
 }, [gri, valores, sempreVisivel, editavel]);

 if (linhas.length === 0) return null;

 // Sem responseId não há em que PATCH gravar — cai pro modo leitura mesmo
 // que o chamador tenha passado `editavel` (ex.: questão ainda sem resposta
 // nenhuma, onde `id_question_response` não existe).
 const podeEditar = editavel && !!responseId;
 // Mesmo caso, mas avisado: sem isso o avaliador via só o "—" mudo e achava
 // que era bug. Consertar de verdade exigiria criar uma question_response
 // vazia (ou desacoplar question_response_gri dela) — descartado por ora:
 // ~20 pontos de query no backend tratam "existe question_response" como
 // "respondida" (progresso, e-mail de questionário concluído, lembrete de
 // prazo), risco maior que o ganho. Avisar já resolve a confusão do usuário.
 const aguardandoResposta = editavel && !responseId;

 return (
  <Box
   bg={flat ? 'transparent' : '#F7FBF8'}
   border={flat ? 'none' : '1px solid #CDE8D6'}
   borderRadius={flat ? 0 : '8px'}
   p={flat ? 0 : '12px'}
   mt={flat ? 0 : '12px'}
  >
   {flat ? (
    <Text sx={AUDIT_LABEL} mb='8px'>
     Medidas dos indicadores
    </Text>
   ) : (
    <Text fontSize='11px' fontWeight={700} color='#1B5E30' mb='8px'>
     MEDIDAS DOS INDICADORES
    </Text>
   )}

   {aguardandoResposta && (
    <Flex
     align='center'
     gap='6px'
     fontSize='12px'
     fontWeight={600}
     color={flat ? AUDIT.statusAttention : '#B23A12'}
     mb='8px'
    >
     {flat && <Box as={FiLock} boxSize='12px' flexShrink={0} />}
     <Text>
      Pergunta ainda não respondida — os indicadores só podem ser preenchidos
      pelo avaliador depois que a empresa responder.
     </Text>
    </Flex>
   )}

   {podeEditar
    ? linhas.map((linha) => (
       <MedidaDoAvaliadorField
        key={linha.id_gri_indicator}
        indicador={linha}
        valorInicial={linha.value ?? ''}
        responseId={responseId}
        idReviewer={idReviewer}
        isDisabled={isDisabled}
       />
      ))
    : linhas.map((linha) => (
    <Flex
     key={linha.id_gri_indicator}
     justify='space-between'
     align='baseline'
     gap='12px'
     py='4px'
    >
     <Text fontSize='13px' color={flat ? AUDIT.inkMuted : '#5C6066'}>
      <Text as='span' fontWeight={600} color={flat ? AUDIT.ink : '#16262E'}>
       {linha.code}
      </Text>
      {linha.name ? ` — ${linha.name}` : ''}
     </Text>

     <Text
      fontSize='14px'
      fontWeight={700}
      color={flat ? AUDIT.ink : '#16262E'}
      whiteSpace='nowrap'
     >
      {formatar(linha.value)}
      {linha.value != null && linha.value !== '' && linha.unit
       ? ` ${linha.unit}`
       : ''}
     </Text>
    </Flex>
   ))}
  </Box>
 );
};

export default MedidasGri;
